/**
 * People: who is in the organisation, by name, so a workbench, the console or the phone can pick
 * a teammate without ever showing a bare object id. AIOE's own contract; the workbench copies it
 * into `shared/schemas/people.ts`.
 *
 *   GET /people/v1/directory?q=<2+ characters>&limit=20&projectId=   a person's sign-in or a desktop's device token
 *       → { people: [{ id, name, email?, onProject?, linked? }], more, sources }
 *   GET /people/v1/me                                                 a person's sign-in or a desktop's device token
 *       → { id, name?, email?, picture?, profileUrl? }
 *
 * `id` is the subject id a project member carries (`members[].subjectId`): an Entra object id on
 * Enterprise, the person's Cloud id on Mojo Up AI Cloud. Sources: `aioe` is everyone who has
 * signed in to this organisation (name and email as their sign-in gave them); `members` is the
 * Cloud organisation's member list; `entra` is the Microsoft Entra directory, searched as the
 * AIOE API application when it holds the Entra client secret and `User.Read.All` (otherwise the
 * answer says so by leaving the source out, and no error is raised). Name and email are matched
 * case-insensitively on what was typed; a result list never exceeds `limit`, and `more` says
 * there were others. Reads are audited by query length and count, never by what was typed.
 *
 * Pictures (D243, optional, no version bump): `picture` on a directory person and on a project member
 * is the absolute address of `GET /people/v1/:id/picture`, a proxied image (PNG, JPEG, GIF or WebP, at most
 * 256 KB, `cache-control: private, max-age=86400`, ETag, 304 on If-None-Match, 404 when there is none) for
 * any person of the caller's organisation. It needs the caller's token, so a client fetches it and shows
 * initials on a 404. `GET /people/v1/me` `picture` is unchanged.
 *
 * Project members (`projects.ts` `projectMemberSchema`) carry `name` and `email` on every answer,
 * resolved the same way, so a picker built from members shows names too. Words for people:
 * person, teammate, on this project.
 *
 * Linked identities (`linked`, 14 October 2026): the accounts a person linked at Settings → You →
 * Your Linked Accounts (`/source/v1/identities`), by name only, beside each person the caller may
 * already see (directory results and project members). No token and no other id ever; a provider
 * the person has not linked is left out, and so is `linked` when they linked none. A trackers push
 * assigns through the same names (GitHub login, Azure DevOps uniqueName, Jira accountId).
 * `profileUrl` on `/people/v1/me` is the console page where the person links them.
 */
import { z } from "zod";
export const PEOPLE_DIRECTORY_MIN_QUERY = 2;
export const PEOPLE_DIRECTORY_MAX_LIMIT = 50;
export const PEOPLE_DIRECTORY_DEFAULT_LIMIT = 20;
export const DIRECTORY_SOURCES = ["aioe", "members", "entra"];
/** A person's linked source-control and tracker accounts, by name only (never a token). */
export const linkedIdentitiesSchema = z.object({
    /** The GitHub login, as an issue's assignee takes it. */
    github: z.object({ login: z.string().min(1).max(200) }).optional(),
    /** Azure DevOps: `uniqueName` is what `System.AssignedTo` takes (the sign-in's UPN or email). */
    azureDevOps: z.object({ uniqueName: z.string().min(1).max(320), displayName: z.string().max(300).optional() }).optional(),
    /** Jira Cloud: the Atlassian `accountId` an issue's assignee takes. */
    jira: z.object({ accountId: z.string().min(1).max(200), displayName: z.string().max(300).optional() }).optional(),
});
export const directoryPersonSchema = z.object({
    /** The subject id a project member carries. */
    id: z.string().min(1).max(200),
    /** Never empty: the name, else the email, else "Someone (…last six of the id)". */
    name: z.string().min(1).max(300),
    email: z.string().max(320).optional(),
    /** Optional, no version bump (D243): the address of `GET /people/v1/:id/picture` on this platform (https, absolute), present when the person is known to have a picture or the platform cannot cheaply know; that route answers 404 for none, and the client then shows initials. */
    picture: z.string().url().max(2000).optional(),
    /** Set when `projectId` was given: whether they are already a member, directly or through a group. Only members may be picked as teammates; the rest are shown as "not on the project yet". */
    onProject: z.boolean().optional(),
    /** The accounts they linked, by name only; absent when they linked none. */
    linked: linkedIdentitiesSchema.optional(),
});
/** `GET /people/v1/me`: the person behind the token, as this organisation vouches for them (api people/identity.ts). */
export const peopleMeSchema = z.object({
    id: z.string().min(1).max(200),
    name: z.string().max(300).optional(),
    /** Verified: the email this organisation (or a Cloud sign-in method) proved. */
    email: z.string().max(320).optional(),
    picture: z.string().optional(),
    /** The console page where the person links GitHub and Azure DevOps (and Jira, where the organisation connected it): `<console>/settings#linked-accounts`. */
    profileUrl: z.string().max(2000).optional(),
});
/** The name to show for a person when nothing better is known: never a bare id. */
export function displayNameOf(p) {
    return p.name?.trim() || p.email?.trim() || `Someone (…${p.id.slice(-6)})`;
}
//# sourceMappingURL=people.js.map